import { CloserLoaderComponent } from "@fidget/common/components/loader/closerLoader";
import { staticConfig } from "@fidget/common/config/static-config";
import classNames from "classnames";
import React, { useState } from "react";
import DropZone from "react-dropzone";
import { Avatar } from "@fidget/common/components/avatar/avatar";
import { ImageComponent } from "@fidget/common/components/image/image";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { useStore } from "../../../models";
import { IUserProfile } from "../../../models/currentUser/UserProfile";
import { DiscardApplyButtons } from "../discardApplyButtons";
import styles from "./avatarSettings.css";
import { AppPermission } from "@closerplatform/spinner-openapi";

export interface AvatarUploadInfo {
  file: File;
  avatarBase64?: string;
  uploaded: boolean;
}

export interface Props {
  userProfile: IUserProfile;
}

export const AvatarSettings = ({ userProfile }: Props) => {
  const { uiStore, currentUserStore } = useStore();

  const [editAvatar, setEditAvatar] = useState(false);
  const [uploadAvatar, setUploadAvatar] = useState<AvatarUploadInfo>();
  const [toBigError, setToBigError] = useState(false);
  const [uploading, setUploading] = useState(false);

  const canEdit = userProfile.doesHavePermission(AppPermission.UpdatePersonalData);

  const loadAvatar = (avatarPicture: File) => {
    setToBigError(false);
    setUploadAvatar(undefined);

    if (avatarPicture.size > staticConfig.maxPictureFileSizeBytes) {
      setToBigError(true);
      uiStore.addErrorToast(intl.formatMessage("settings.profile.picture.error.tooBig"));

      return;
    }
    let reader = new FileReader();
    reader.onload = (event: any) => {
      const avatarBase64 = event.target.result;
      if (uploadAvatar && uploadAvatar.avatarBase64 === avatarBase64) {
        return;
      }
      const avatar = { file: avatarPicture, avatarBase64, uploaded: true } as AvatarUploadInfo;
      setUploadAvatar(avatar);
    };
    reader.readAsDataURL(avatarPicture);
  };

  const onAvatarAdded = (pictures: File[], rejectedFiles: File[]) => {
    const picture = pictures[0];
    if (picture) {
      loadAvatar(picture);
    }
    if (rejectedFiles.length) {
      uiStore.addErrorToast(intl.formatMessage("settings.profile.picture.error.wrongFormat"));
    }
  };

  const deleteAvatar = () => {
    userProfile
      .deleteAvatar(currentUserStore.orgConfig?.defaultAgentAvatarToken)
      .then(() => {
        uiStore.addSuccessToast(intl.formatMessage("settings.profile.picture.success"));
        cancelAvatarEditing();
      })
      .catch(() => {
        uiStore.addGenericErrorToast();
      });
  };

  const startAvatarEditing = () => {
    setEditAvatar(true);
  };

  const cancelAvatarEditing = () => {
    setEditAvatar(false);
    setUploadAvatar(undefined);
    setToBigError(false);
  };

  const setAvatar = () => {
    if (uploadAvatar) {
      setUploading(true);

      userProfile
        .updateAvatar(uploadAvatar.file)
        .then(() => {
          uiStore.addSuccessToast(intl.formatMessage("settings.profile.picture.success"));
          setUploading(false);
          cancelAvatarEditing();
        })
        .catch(() => {
          uiStore.addGenericErrorToast();
        });
    }
  };

  const renderAvatarDisplay = () => {
    return (
      <div className={styles["avatar-section"]}>
        <div
          className={classNames(styles.avatar, { [styles["avatar-disabled"]]: !canEdit })}
          onClick={() => canEdit && startAvatarEditing()}
        >
          <Avatar user={userProfile} />
        </div>
        {canEdit && (
          <div className={styles["avatar-info"]}>
            <i className={[styles.icon, "icon-pencil"].join(" ")} />
            <L10n id={"settings.profile.picture.change"} />
          </div>
        )}
      </div>
    );
  };

  const renderAvatarEdit = () => {
    return (
      <form
        onSubmit={(ev) => ev.preventDefault()}
        className={classNames(styles["avatar-section"], { [styles.loading]: uploading })}
      >
        <DropZone
          className={styles.avatar}
          activeClassName={styles.active}
          onDrop={onAvatarAdded}
          accept={"image/jpeg, image/png"}
        >
          {(uploadAvatar && <ImageComponent src={uploadAvatar.avatarBase64} />) || (
            <Avatar user={userProfile} />
          )}
          {uploading && (
            <div className={styles.loader}>
              <CloserLoaderComponent />
            </div>
          )}
          <div className={styles["dragdrop-info"]}>
            <div className={styles["bordered-container"]}>
              <div>
                <i className={classNames("icon-upload", styles["upload-icon"])} />
                <L10n id={"settings.profile.picture.drag"} />
              </div>
            </div>
          </div>
        </DropZone>
        <div className={classNames(styles["avatar-info"], styles.display)}>
          <i className={[styles.icon, "icon-pencil"].join(" ")} />
        </div>
        <div className={classNames(styles["avatar-info"], styles.display)}>
          <div className={styles.title} onClick={deleteAvatar}>
            <L10n id={"settings.profile.picture.remove"} />
          </div>
          <div className={styles.description}>
            <L10n id={"settings.profile.picture.remove.description"} />
          </div>
          <div className={styles.title}>
            <L10n id={"settings.profile.picture.upload"} />
            <input
              type="file"
              onChange={(e) => e.target.files && loadAvatar(e.target.files[0]!)}
              accept="image/png, image/jpeg"
            />
          </div>
          <div className={classNames(styles.description, { [styles.error]: toBigError })}>
            <L10n id={"settings.profile.picture.upload.description"} />
          </div>
          <DiscardApplyButtons
            onDiscard={cancelAvatarEditing}
            onApply={setAvatar}
            applyDisabled={!(uploadAvatar && uploadAvatar.uploaded)}
            alignToLeft
          />
        </div>
      </form>
    );
  };

  if (canEdit && editAvatar) {
    return renderAvatarEdit();
  }

  return renderAvatarDisplay();
};
